import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';

// Credit threshold constants
const THRESHOLD_LOW_500 = 500;
const THRESHOLD_LOW_100 = 100;

// Local storage keys for tracking alert state
const KEY_ALERTED_500 = 'mellow_credit_alerted_500';
const KEY_ALERTED_100 = 'mellow_credit_alerted_100';

/**
 * Hook to fetch user's credit balance
 * Uses shared user_permanent_credits table (credits are shared across apps)
 * Includes smart threshold tracking with reset on top-up
 */
export function useCreditBalance() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevBalanceRef = useRef(null);

  useEffect(() => {
    fetchBalance();

    // Refresh every 30 seconds
    const interval = setInterval(fetchBalance, 30000);
    return () => clearInterval(interval);
  }, []);

  async function fetchBalance() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_permanent_credits')
        .select('credit_balance')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {  // PGRST116 = no rows
        console.error('Error fetching credit balance:', error);
      }

      // Default 1000 for new users (will be created on first LLM call)
      const newBalance = data?.credit_balance ?? 1000;
      const prevBalance = prevBalanceRef.current;

      // Check for top-up: balance increased from below threshold
      // Reset alert flags so user gets alerted again when they drop below threshold next time
      if (prevBalance !== null && newBalance > prevBalance) {
        // User topped up - reset alert flags if they were below thresholds
        if (prevBalance < THRESHOLD_LOW_500 && newBalance >= THRESHOLD_LOW_500) {
          localStorage.removeItem(KEY_ALERTED_500);
          console.log('[Credits] Reset 500 alert flag after top-up');
        }
        if (prevBalance < THRESHOLD_LOW_100 && newBalance >= THRESHOLD_LOW_100) {
          localStorage.removeItem(KEY_ALERTED_100);
          console.log('[Credits] Reset 100 alert flag after top-up');
        }
      }

      // Check for threshold crossing and fire events (first time only)
      if (newBalance < THRESHOLD_LOW_500 && !localStorage.getItem(KEY_ALERTED_500)) {
        trackEvent('credit_low_500', {
          balance: newBalance,
          user_id: user.id,
        });
        localStorage.setItem(KEY_ALERTED_500, 'true');
        console.log('[Credits] Fired credit_low_500 event');
      }

      if (newBalance < THRESHOLD_LOW_100 && !localStorage.getItem(KEY_ALERTED_100)) {
        trackEvent('credit_low_100', {
          balance: newBalance,
          user_id: user.id,
        });
        localStorage.setItem(KEY_ALERTED_100, 'true');
        console.log('[Credits] Fired credit_low_100 event');
      }

      // Store for next comparison
      prevBalanceRef.current = newBalance;
      setBalance(newBalance);
      setLoading(false);
    } catch (err) {
      console.error('Error in fetchBalance:', err);
      setLoading(false);
    }
  }

  return { balance, loading, refresh: fetchBalance };
}

export default useCreditBalance;

